"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Alert,
  Rating,
  Snackbar,
} from "@mui/material";
import {
  Calculate,
  Description,
  Checklist,
  AccountTree,
  Build,
  Security,
  Download,
  // Link as LinkIcon,
  // BuildOutlined,
  Close,
  CheckCircle,
  Preview,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  increment,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Tool } from "@/types/resources";
import PopoverForm from "@/components/PopoverForm";

export const runtime = "edge";

// Icon mapping
const iconMap = {
  Calculate: <Calculate sx={{ fontSize: 40 }} />,
  Description: <Description sx={{ fontSize: 40 }} />,
  Checklist: <Checklist sx={{ fontSize: 40 }} />,
  AccountTree: <AccountTree sx={{ fontSize: 40 }} />,
  Build: <Build sx={{ fontSize: 40 }} />,
  Security: <Security sx={{ fontSize: 40 }} />,
};

const fetchTools = async (): Promise<Tool[]> => {
  try {
    const q = query(
      collection(db, "tools"),
      where("isActive", "==", true),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt:
        doc.data().createdAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
      updatedAt:
        doc.data().updatedAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
      lastDownloadedAt:
        doc.data().lastDownloadedAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
    })) as Tool[];
  } catch (error) {
    console.error("Error fetching tools:", error);
    return [];
  }
};

const fetchRecommendedTools = async (
  currentToolId: string,
  type: string
): Promise<Tool[]> => {
  try {
    const q = query(
      collection(db, "tools"),
      where("isActive", "==", true),
      where("type", "==", type),
      where("id", "!=", currentToolId),
      orderBy("downloadCount", "desc"),
      limit(3)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Tool[];
  } catch (error) {
    console.error("Error fetching recommended tools:", error);
    return [];
  }
};

const checkDownloadEligibility = async (
  email: string,
  resourceId: string
): Promise<boolean> => {
  try {
    const q = query(
      collection(db, "downloads"),
      where("email", "==", email),
      where("resourceId", "==", resourceId),
      where(
        "downloadedAt",
        ">",
        new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      )
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  } catch (error) {
    console.error("Error checking download eligibility:", error);
    return true; // Fallback to allow download if check fails
  }
};

const recordDownload = async (
  resourceId: string,
  userData: { name: string; email: string; company?: string },
  resourceType: "tool" | "whitepaper"
) => {
  try {
    // Record the download
    await addDoc(collection(db, "downloads"), {
      resourceId,
      resourceType,
      ...userData,
      downloadedAt: new Date().toISOString(),
      success: true,
    });

    // Increment download count and update last downloaded
    const resourceRef = doc(
      db,
      resourceType === "tool" ? "tools" : "whitepapers",
      resourceId
    );
    await updateDoc(resourceRef, {
      downloadCount: increment(1),
      lastDownloadedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    console.log("Download recorded successfully");
    return true;
  } catch (error) {
    console.error("Error recording download:", error);

    // Record failed attempt
    try {
      await addDoc(collection(db, "download_attempts"), {
        resourceId,
        resourceType,
        ...userData,
        attemptedAt: new Date().toISOString(),
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } catch (logError) {
      console.error("Error logging download attempt:", logError);
    }

    throw error;
  }
};

// Simple time formatter without date-fns
const formatTimeAgo = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000)
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  } catch {
    return "recently";
  }
};

export default function Tools() {
  const theme = useTheme();
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [previewTool, setPreviewTool] = useState<Tool | null>(null);
  const [recommendedTools, setRecommendedTools] = useState<Tool[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
  });
  const [earlyInterestEmail, setEarlyInterestEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    const loadTools = async () => {
      try {
        const data = await fetchTools();
        setTools(data);
      } catch (error) {
        console.error("Error loading tools:", error);
        setError("Failed to load tools. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadTools();
  }, []);

  useEffect(() => {
    if (submitted && selectedTool) {
      const loadRecommendations = async () => {
        const recommendations = await fetchRecommendedTools(
          selectedTool.id,
          selectedTool.type
        );
        setRecommendedTools(recommendations);
      };
      loadRecommendations();
    }
  }, [submitted, selectedTool]);

  const getTypeColor = (type: string) => {
    const colors = {
      calculator: theme.palette.primary.main,
      template: theme.palette.secondary.main,
      checklist: theme.palette.success.dark,
      kit: theme.palette.warning.main,
    };
    return colors[type as keyof typeof colors] || theme.palette.primary.main;
  };

  const validateForm = () => {
    const errors = {
      name: formData.name.trim() ? "" : "Name is required",
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ? ""
        : "Valid email is required",
    };
    setFormErrors(errors);
    return !errors.name && !errors.email;
  };

  const validateEarlyInterest = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(earlyInterestEmail);
  };

  const handleToolAction = (tool: Tool) => {
    if (tool.fileUrl) {
      setSelectedTool(tool);
      setSubmitted(false);
      setFormData({ name: "", email: "", company: "" });
      setError(null);
    } else if (tool.action.href) {
      window.open(tool.action.href, "_blank");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTool) return;

    if (!validateForm()) {
      setError("Please fix the form errors before submitting.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Check download eligibility
      const isEligible = await checkDownloadEligibility(
        formData.email,
        selectedTool.id
      );
      if (!isEligible) {
        setError(
          "You've already downloaded this resource in the last 24 hours."
        );
        setSubmitting(false);
        return;
      }

      // 1️⃣ Send data to Airtable
      const airtableResponse = await fetch("/api/submitToAirtable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          consent: true,
          interest: "Tool & Template Download",
          resourceTitle: selectedTool.title,
        }),
      });

      if (!airtableResponse.ok) {
        throw new Error("Failed to submit data to Airtable.");
      }

      // 2️⃣ Record download in Firestore
      await recordDownload(selectedTool.id, formData, "tool");

      // 3️⃣ Mark submission successful
      setSubmitted(true);

      // 4️⃣ Trigger download with small delay
      setTimeout(() => {
        if (selectedTool.fileUrl) {
          const link = document.createElement("a");
          link.href = selectedTool.fileUrl;
          link.download = `${selectedTool.title}${getFileExtension(
            selectedTool.fileUrl
          )}`;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }, 1500);
    } catch (error) {
      console.error("Error processing submission or download:", error);
      setError("Failed to process your request. Please try again.");
      setSubmitting(false);
    }
  };

  const handleEarlyInterest = async () => {
    if (!validateEarlyInterest()) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address.",
      });
      return;
    }

    try {
      await fetch("/api/submitToAirtable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: earlyInterestEmail,
          interest: "Early Access - Tools & Templates",
          status: "Early Interest",
        }),
      });

      setEarlyInterestEmail("");
      setSnackbar({
        open: true,
        message: "Thanks! We'll notify you when new tools are available.",
      });
    } catch (error) {
      console.error("Error submitting early interest:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit. Please try again.",
      });
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedTool(null);
      setSubmitted(false);
      setRecommendedTools([]);
    }
  };

  const handlePreviewClose = () => {
    setPreviewTool(null);
  };

  const getFileExtension = (url: string) => {
    const extension = url.split(".").pop()?.split("?")[0];
    return extension ? `.${extension}` : ".zip";
  };

  const calculateRating = (downloadCount: number) => {
    const baseRating = 4.5;
    const reviewCount = Math.floor(downloadCount / 10);
    return {
      rating: baseRating + Math.random() * 0.5,
      reviews: reviewCount || 1,
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading tools...</Typography>
      </Container>
    );
  }

  if (!tools.length) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: 12,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Existing empty state background elements... */}

        <Box sx={{ position: "relative", zIndex: 2 }}>
          {/* Existing empty state content... */}

          {/* Enhanced Early Interest Capture */}
          <Box sx={{ mt: 6, maxWidth: 500, mx: "auto" }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Get Early Access
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Be the first to know when we launch our premium tools and
              templates
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your email"
                value={earlyInterestEmail}
                onChange={(e) => setEarlyInterestEmail(e.target.value)}
                error={!!earlyInterestEmail && !validateEarlyInterest()}
                helperText={
                  earlyInterestEmail && !validateEarlyInterest()
                    ? "Please enter a valid email"
                    : ""
                }
              />
              <Button
                variant="contained"
                onClick={handleEarlyInterest}
                disabled={!validateEarlyInterest()}
                sx={{ whiteSpace: "nowrap" }}
              >
                Notify Me
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary">
              We respect your privacy. No spam, unsubscribe anytime.
            </Typography>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 8 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 3,
          }}
        >
          Tools & Templates
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
        >
          Free, practical resources to help you plan, build, and grow your
          digital presence. Share your details to access our premium tools and
          templates.
        </Typography>
      </Box>

      {/* Tools Grid */}
      <Grid container spacing={4}>
        {tools.map((tool) => {
          const { rating, reviews } = calculateRating(tool.downloadCount || 0);

          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={tool.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}
                  >
                    <Box sx={{ color: getTypeColor(tool.type), mr: 3 }}>
                      {iconMap[tool.icon as keyof typeof iconMap]}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        fontWeight={600}
                        gutterBottom
                      >
                        {tool.title}
                      </Typography>
                      <Chip
                        label={
                          tool.type.charAt(0).toUpperCase() + tool.type.slice(1)
                        }
                        size="small"
                        sx={{
                          backgroundColor: alpha(getTypeColor(tool.type), 0.1),
                          color: getTypeColor(tool.type),
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Social Proof */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Rating value={rating} size="small" readOnly />
                    <Typography variant="body2" color="text.secondary">
                      ({reviews} reviews)
                    </Typography>
                  </Box>

                  <Typography
                    color="text.secondary"
                    sx={{ mb: 3, flexGrow: 1 }}
                  >
                    {tool.description}
                  </Typography>

                  {/* Format Chips */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Available formats:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {tool.format.map((format, index) => (
                        <Chip
                          key={index}
                          label={format}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: "text.primary",
                            fontSize: "0.7rem",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Features List */}
                  <List dense sx={{ mb: 3 }}>
                    {tool.features.slice(0, 3).map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: getTypeColor(tool.type),
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ fontSize: "0.875rem" }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Download Count & Recent Activity */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      📥 Downloaded by{" "}
                      {tool.downloadCount?.toLocaleString() || 0} professionals
                    </Typography>
                    {tool.lastDownloadedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Last download: {formatTimeAgo(tool.lastDownloadedAt)}
                      </Typography>
                    )}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Preview />}
                      onClick={() => setPreviewTool(tool)}
                      size="small"
                      sx={{ flex: 1 }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => handleToolAction(tool)}
                      size="small"
                      sx={{
                        flex: 2,
                        background: `linear-gradient(135deg, ${getTypeColor(
                          tool.type
                        )} 0%, ${alpha(getTypeColor(tool.type), 0.8)} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 25px ${alpha(
                            getTypeColor(tool.type),
                            0.3
                          )}`,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Download Dialog */}
      <Dialog
        open={Boolean(selectedTool)}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            fontWeight: 600,
            pr: 8,
          }}
        >
          Download {selectedTool?.title}
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 16, top: 16, cursor: "pointer" }}
            disabled={submitting}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {!submitted ? (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Please provide your details to download this exclusive
                tool/template. We&apos;ll send the file directly to your email
                and keep you updated with new resources.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setFormErrors({ ...formErrors, name: "" });
                      }}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setFormErrors({ ...formErrors, email: "" });
                      }}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={submitting}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        `Download ${selectedTool?.type}`
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <Box textAlign="center" sx={{ py: 4 }}>
              <CheckCircle
                sx={{
                  fontSize: 64,
                  color: theme.palette.success.dark,
                  mb: 2,
                }}
              />
              <Typography variant="h6" component="div" gutterBottom>
                Download Started!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Thank you for your interest. The {selectedTool?.type} is being
                downloaded and we&apos;ve also sent a copy to your email
                address.
              </Typography>

              {/* Close Button */}
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  mb: 3,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Close
              </Button>

              {/* Recommended Tools */}
              {recommendedTools.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    You Might Also Like
                  </Typography>
                  <List>
                    {recommendedTools.map((tool) => (
                      <ListItem
                        key={tool.id}
                        component="div"
                        onClick={() => {
                          setSelectedTool(tool);
                          setSubmitted(false);
                          setRecommendedTools([]);
                        }}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            ),
                          },
                        }}
                      >
                        <ListItemText
                          primary={tool.title}
                          secondary={tool.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={Boolean(previewTool)}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            fontWeight: 600,
            pr: 8,
          }}
        >
          {previewTool?.title}
          <IconButton
            onClick={handlePreviewClose}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {previewTool && (
            <>
              <Typography variant="h6" gutterBottom>
                What&apos;s Included:
              </Typography>
              <List>
                {previewTool.features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  handlePreviewClose();
                  handleToolAction(previewTool);
                }}
                sx={{ mt: 2 }}
                startIcon={<Download />}
              >
                Download Now
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Call to Action */}
      <Box
        sx={{
          textAlign: "center",
          mt: 8,
          p: 6,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Need Custom Tools or Templates?
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
        >
          We can create bespoke tools, calculators, and templates tailored to
          your specific business needs.
        </Typography>
        <PopoverForm
          triggerElement={
            <Button
              variant="contained"
              size="large"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                px: 4,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Request Custom Solution
            </Button>
          }
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}
